import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Users, Calendar, TrendingUp, Globe, RefreshCw, Eye, Monitor, Smartphone, Tablet, Share2, MapPin, Download } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const PAGE_LABELS = {
  '/': 'Úvod',
  '/o-nas': 'O nás',
  '/sluzby': 'Služby',
  '/realizace': 'Realizace',
  '/kontakt': 'Kontakt',
};

const SOURCE_LABELS = {
  direct: { label: 'Přímý vstup', icon: '🔗', color: 'bg-gray-500' },
  google: { label: 'Google', icon: '🔍', color: 'bg-blue-500' },
  facebook: { label: 'Facebook', icon: '📘', color: 'bg-blue-600' },
  instagram: { label: 'Instagram', icon: '📷', color: 'bg-pink-500' },
  bing: { label: 'Bing', icon: '🌐', color: 'bg-cyan-500' },
  seznam: { label: 'Seznam.cz', icon: '🇨🇿', color: 'bg-red-500' },
  youtube: { label: 'YouTube', icon: '📺', color: 'bg-red-600' },
  twitter: { label: 'Twitter/X', icon: '🐦', color: 'bg-gray-800' },
  linkedin: { label: 'LinkedIn', icon: '💼', color: 'bg-blue-700' },
  tiktok: { label: 'TikTok', icon: '🎵', color: 'bg-black' },
};

const DEVICE_INFO = {
  desktop: { label: 'Počítač', icon: Monitor, color: 'bg-indigo-500' },
  mobile: { label: 'Mobil', icon: Smartphone, color: 'bg-pink-500' },
  tablet: { label: 'Tablet', icon: Tablet, color: 'bg-orange-500' },
};

// ISO country code → name + flag (top countries for Fabrikov)
const COUNTRY_NAMES = {
  CZ: { name: 'Česko', flag: '🇨🇿' },
  SK: { name: 'Slovensko', flag: '🇸🇰' },
  DE: { name: 'Německo', flag: '🇩🇪' },
  AT: { name: 'Rakousko', flag: '🇦🇹' },
  PL: { name: 'Polsko', flag: '🇵🇱' },
  HU: { name: 'Maďarsko', flag: '🇭🇺' },
  GB: { name: 'Velká Británie', flag: '🇬🇧' },
  US: { name: 'USA', flag: '🇺🇸' },
  FR: { name: 'Francie', flag: '🇫🇷' },
  IT: { name: 'Itálie', flag: '🇮🇹' },
  ES: { name: 'Španělsko', flag: '🇪🇸' },
  NL: { name: 'Nizozemsko', flag: '🇳🇱' },
  UA: { name: 'Ukrajina', flag: '🇺🇦' },
  unknown: { name: 'Neznámá', flag: '🌍' },
};

const buildCsv = (data) => {
  const rows = [['Sekce', 'Položka', 'Zobrazení', 'Unikátní návštěvníci']];
  rows.push(['Online', 'Právě teď', data.online, '']);
  [['Dnes', data.today], ['7 dní', data.week], ['30 dní', data.month], ['365 dní', data.year]].forEach(([l, v]) =>
    rows.push(['Období', l, v.visits, v.unique]));
  rows.push(['Období', 'Celkem', data.total, '']);
  data.daily_chart.forEach((d) => rows.push(['Den', d.date, d.visits, d.unique]));
  data.top_pages.forEach((p) => rows.push(['Stránka (30 dní)', PAGE_LABELS[p.path] || p.path, p.visits, '']));
  (data.sources || []).forEach((s) => rows.push(['Zdroj (30 dní)', SOURCE_LABELS[s.source]?.label || s.source, s.visits, '']));
  (data.devices || []).forEach((d) => rows.push(['Zařízení (30 dní)', DEVICE_INFO[d.device]?.label || d.device, d.visits, '']));
  (data.countries || []).forEach((c) => rows.push(['Země (30 dní)', COUNTRY_NAMES[c.country]?.name || c.country, c.visits, '']));
  const esc = (v) => `"${String(v).replace(/"/g, '""')}"`;
  return '\uFEFF' + rows.map((r) => r.map(esc).join(';')).join('\r\n');
};

const downloadCsv = (data) => {
  const blob = new Blob([buildCsv(data)], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `fabrikov-navstevnost-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
};

const StatCard = ({ icon: Icon, label, value, sub, color = 'green', testId }) => (
  <div
    data-testid={testId}
    className={`bg-white dark:bg-gray-900 border-l-4 border-${color}-500 border-t border-r border-b border-gray-200 dark:border-gray-800 rounded-xl p-5 shadow-sm`}
  >
    <div className="flex items-center justify-between mb-2">
      <span className="text-sm font-medium text-gray-600 dark:text-gray-400">{label}</span>
      <Icon size={20} className={`text-${color}-500`} />
    </div>
    <div className="text-3xl font-bold text-gray-900 dark:text-white">{value}</div>
    {sub && <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">{sub}</div>}
  </div>
);

const Analytics = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const fetchAnalytics = async (showRefreshing = false) => {
    if (showRefreshing) setRefreshing(true);
    try {
      const res = await axios.get('/api/admin/analytics');
      setData(res.data);
    } catch (e) {
      toast.error('Chyba při načítání statistik');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchAnalytics();
    // Auto-refresh every 30 seconds for "online users"
    const interval = setInterval(() => fetchAnalytics(false), 30000);
    return () => clearInterval(interval);
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-black flex items-center justify-center">
        <div className="text-gray-600 dark:text-gray-400">Načítám statistiky...</div>
      </div>
    );
  }

  if (!data) return null;

  const maxVisits = Math.max(...data.daily_chart.map((d) => d.visits), 1);

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-black px-4 py-8 sm:px-6 lg:px-8" data-testid="analytics-page">
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-8 gap-4">
          <div>
            <Link
              to="/admin/dashboard"
              data-testid="back-to-dashboard"
              className="inline-flex items-center text-green-600 dark:text-green-400 hover:underline mb-2 text-sm"
            >
              <ArrowLeft size={16} className="mr-1" />
              Zpět na dashboard
            </Link>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
              Návštěvnost webu
            </h1>
            <p className="text-gray-600 dark:text-gray-400 text-sm mt-1">
              Auto-refresh každých 30s • Anonymní GDPR-friendly tracking
            </p>
          </div>
          <div className="flex gap-2">
          <button
            onClick={() => downloadCsv(data)}
            data-testid="export-csv-btn"
            className="flex items-center space-x-2 bg-gray-900 hover:bg-gray-700 dark:bg-white dark:hover:bg-gray-200 text-white dark:text-black px-4 py-2 rounded-lg transition-colors duration-200"
          >
            <Download size={18} />
            <span>Stáhnout CSV</span>
          </button>
          <button
            onClick={() => fetchAnalytics(true)}
            data-testid="refresh-btn"
            className="flex items-center space-x-2 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg"
          >
            <RefreshCw size={18} className={refreshing ? 'animate-spin' : ''} />
            <span>Aktualizovat</span>
          </button>
          </div>
        </div>

        {/* Online users - prominent card */}
        <div className="mb-8 bg-gradient-to-r from-green-500 to-emerald-600 rounded-xl p-6 text-white shadow-lg" data-testid="online-card">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center space-x-2 mb-1">
                <span className="relative flex h-3 w-3">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-3 w-3 bg-white"></span>
                </span>
                <span className="text-sm font-medium opacity-90">PRÁVĚ TEĎ ONLINE</span>
              </div>
              <div className="text-5xl font-bold">{data.online}</div>
              <div className="text-sm opacity-90 mt-1">
                {data.online === 1 ? 'návštěvník' : data.online >= 2 && data.online <= 4 ? 'návštěvníci' : 'návštěvníků'} na webu
              </div>
            </div>
            <Users size={64} className="opacity-30" />
          </div>
        </div>

        {/* Period stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <StatCard
            icon={Calendar}
            label="Dnes"
            value={data.today.visits}
            sub={`${data.today.unique} unikátních`}
            color="blue"
            testId="stat-today"
          />
          <StatCard
            icon={Calendar}
            label="Tento týden"
            value={data.week.visits}
            sub={`${data.week.unique} unikátních`}
            color="purple"
            testId="stat-week"
          />
          <StatCard
            icon={Calendar}
            label="Tento měsíc"
            value={data.month.visits}
            sub={`${data.month.unique} unikátních`}
            color="orange"
            testId="stat-month"
          />
          <StatCard
            icon={TrendingUp}
            label="Tento rok"
            value={data.year.visits}
            sub={`${data.year.unique} unikátních`}
            color="green"
            testId="stat-year"
          />
        </div>

        {/* Daily chart */}
        <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-6 mb-8 shadow-sm" data-testid="daily-chart">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-1">
            Posledních 14 dní
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
            Počet zobrazení stránek za den
          </p>
          <div className="flex items-end gap-2 h-48">
            {data.daily_chart.map((d) => {
              const heightPct = (d.visits / maxVisits) * 100;
              const date = new Date(d.date);
              const dayLabel = date.toLocaleDateString('cs-CZ', { day: 'numeric', month: 'numeric' });
              return (
                <div key={d.date} className="flex-1 flex flex-col items-center group relative">
                  <div className="absolute -top-8 bg-gray-900 dark:bg-gray-700 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-10">
                    {d.visits} návštěv ({d.unique} uniq.)
                  </div>
                  <div
                    className="w-full bg-gradient-to-t from-green-600 to-green-400 dark:from-green-500 dark:to-green-300 rounded-t hover:from-green-700 hover:to-green-500 transition-all min-h-[2px]"
                    style={{ height: `${heightPct}%` }}
                  />
                  <div className="text-xs text-gray-500 dark:text-gray-400 mt-2 transform -rotate-45 origin-top-left whitespace-nowrap">
                    {dayLabel}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Sources + Devices */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Traffic Sources */}
          <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-6 shadow-sm" data-testid="sources-card">
            <div className="flex items-center gap-2 mb-1">
              <Share2 size={20} className="text-green-500" />
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">Zdroje návštěv</h2>
            </div>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">Odkud zákazníci přicházejí (30 dní)</p>
            {(data.sources || []).length === 0 ? (
              <p className="text-gray-500 dark:text-gray-400 text-sm">Zatím žádná data</p>
            ) : (
              <div className="space-y-3">
                {data.sources.map((s) => {
                  const info = SOURCE_LABELS[s.source] || { label: s.source, icon: '🌐', color: 'bg-gray-400' };
                  const total = data.sources.reduce((sum, x) => sum + x.visits, 0);
                  const pct = total ? Math.round((s.visits / total) * 100) : 0;
                  const barPct = total ? (s.visits / data.sources[0].visits) * 100 : 0;
                  return (
                    <div key={s.source}>
                      <div className="flex justify-between items-center mb-1">
                        <span className="text-sm font-medium text-gray-900 dark:text-white flex items-center gap-2">
                          <span className="text-lg">{info.icon}</span>
                          <span className="truncate">{info.label}</span>
                        </span>
                        <span className="text-sm text-gray-600 dark:text-gray-400 whitespace-nowrap ml-2">
                          {s.visits} ({pct}%)
                        </span>
                      </div>
                      <div className="w-full bg-gray-100 dark:bg-gray-800 rounded-full h-2 overflow-hidden">
                        <div className={`${info.color} h-full rounded-full`} style={{ width: `${barPct}%` }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Devices */}
          <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-6 shadow-sm" data-testid="devices-card">
            <div className="flex items-center gap-2 mb-1">
              <Smartphone size={20} className="text-green-500" />
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">Zařízení</h2>
            </div>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">Mobil vs Tablet vs Počítač (30 dní)</p>
            {(data.devices || []).length === 0 ? (
              <p className="text-gray-500 dark:text-gray-400 text-sm">Zatím žádná data</p>
            ) : (
              <div className="grid grid-cols-3 gap-3">
                {['desktop', 'mobile', 'tablet'].map((dev) => {
                  const info = DEVICE_INFO[dev];
                  const Icon = info.icon;
                  const entry = data.devices.find((x) => x.device === dev) || { visits: 0 };
                  const total = data.devices.reduce((sum, x) => sum + x.visits, 0);
                  const pct = total ? Math.round((entry.visits / total) * 100) : 0;
                  return (
                    <div key={dev} className="text-center p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                      <div className={`inline-flex items-center justify-center w-12 h-12 ${info.color} text-white rounded-full mb-2`}>
                        <Icon size={24} />
                      </div>
                      <div className="text-2xl font-bold text-gray-900 dark:text-white">{pct}%</div>
                      <div className="text-xs text-gray-600 dark:text-gray-400 mt-1">{info.label}</div>
                      <div className="text-xs text-gray-500 dark:text-gray-500">{entry.visits} návštěv</div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Countries */}
        <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-6 mb-8 shadow-sm" data-testid="countries-card">
          <div className="flex items-center gap-2 mb-1">
            <MapPin size={20} className="text-green-500" />
            <h2 className="text-xl font-bold text-gray-900 dark:text-white">Země návštěvníků</h2>
          </div>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">Top 10 zemí (30 dní)</p>
          {(data.countries || []).length === 0 ? (
            <p className="text-gray-500 dark:text-gray-400 text-sm">Zatím žádná data</p>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
              {data.countries.map((c) => {
                const info = COUNTRY_NAMES[c.country] || { name: c.country, flag: '🌐' };
                const total = data.countries.reduce((sum, x) => sum + x.visits, 0);
                const pct = total ? Math.round((c.visits / total) * 100) : 0;
                return (
                  <div key={c.country} className="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg flex items-center gap-3">
                    <div className="text-3xl">{info.flag}</div>
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-semibold text-gray-900 dark:text-white truncate">{info.name}</div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        {c.visits} ({pct}%)
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Top pages + Total */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-6 shadow-sm" data-testid="top-pages">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
              Nejnavštěvovanější stránky (30 dní)
            </h2>
            {data.top_pages.length === 0 ? (
              <p className="text-gray-500 dark:text-gray-400 text-sm">Zatím žádné záznamy</p>
            ) : (
              <div className="space-y-3">
                {data.top_pages.map((p, idx) => {
                  const label = PAGE_LABELS[p.path] || p.path;
                  const maxVal = data.top_pages[0].visits;
                  const pct = (p.visits / maxVal) * 100;
                  return (
                    <div key={p.path}>
                      <div className="flex justify-between items-center mb-1">
                        <span className="text-sm font-medium text-gray-900 dark:text-white">
                          {idx + 1}. {label}
                        </span>
                        <span className="text-sm text-gray-600 dark:text-gray-400">
                          {p.visits} návštěv
                        </span>
                      </div>
                      <div className="w-full bg-gray-100 dark:bg-gray-800 rounded-full h-2 overflow-hidden">
                        <div
                          className="bg-green-500 h-full rounded-full"
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-6 shadow-sm flex flex-col justify-center" data-testid="total-card">
            <div className="text-center">
              <Globe size={48} className="text-green-500 mx-auto mb-3" />
              <div className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">
                Celkem za celou dobu
              </div>
              <div className="text-4xl font-bold text-gray-900 dark:text-white">
                {data.total.toLocaleString('cs-CZ')}
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-400 mt-1 flex items-center justify-center gap-1">
                <Eye size={12} /> zobrazení stránek
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Analytics;
